import { useState } from 'react';
import { AreaChart, Area, BarChart, Bar, LabelList, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import Header from '../components/layout/Header.jsx';
import { MultiComboboxSelect } from '../components/ui/MultiComboboxSelect.jsx';
import { useDashboard } from '../features/metricas/useMetricas.js';
import { useVendedores } from '../features/usuarios/useUsuarios.js';
import { useAuthStore } from '../stores/authStore.js';

const MESES = (() => {
  const meses = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
  const now = new Date();
  return Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    return { value: `${meses[d.getMonth()]} ${d.getFullYear()}`, label: `${meses[d.getMonth()]} ${d.getFullYear()}` };
  });
})();

const COLORES_ESTADO = {
  'Compromiso':         '#2563EB',
  'Lo pensará':         '#D97706',
  'Llamar':             '#4F46E5',
  'Contraturno':        '#7C3AED',
  'Convenio':           '#0891B2',
  'No contesta':        '#9CA3AF',
  'Ingreso':            '#059669',
  'Reincorporación':    '#10B981',
  'No quiere hacerlo':  '#DC2626',
};

const COLORES_ORIGEN = ['#0D2B5C', '#1D4ED8', '#60A5FA', '#0891B2', '#D97706', '#6D28D9', '#94A3B8'];

function formatFechaSemana(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' });
}

const tooltipStyle = {
  contentStyle: { fontSize: 12, fontFamily: 'Inter, sans-serif', border: '1px solid #e5e7eb', borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,.06)' },
  itemStyle: { color: '#374151' },
  labelStyle: { color: '#6B7280', fontWeight: 500 },
};

export default function DashboardPage() {
  const usuario = useAuthStore((s) => s.usuario);
  const esAdmin = usuario?.rol === 'admin';
  const esViewer = usuario?.rol === 'viewer';
  const vendedores = useVendedores();

  const [filters, setFilters] = useState({ mes_periodo: [], vendedor_id: [] });
  const activeFilters = {
    mes_periodo: filters.mes_periodo.length ? filters.mes_periodo : undefined,
    vendedor_id: filters.vendedor_id.length ? filters.vendedor_id : undefined,
  };
  const { data, isLoading } = useDashboard(activeFilters);

  const objetivo = data?.objetivo;
  const progreso = objetivo?.valor_global > 0
    ? Math.min(100, Math.round((data.conversiones / objetivo.valor_global) * 100))
    : 0;
  const progresoVendedor = objetivo?.valor_por_vendedor > 0
    ? Math.min(100, Math.round((data.conversiones / objetivo.valor_por_vendedor) * 100))
    : 0;
  const progresoMostrar = usuario?.rol === 'vendedor' ? progresoVendedor : progreso;
  const objetivoMostrar = usuario?.rol === 'vendedor' ? objetivo?.valor_por_vendedor : objetivo?.valor_global;

  const hayFiltros = filters.mes_periodo.length > 0 || filters.vendedor_id.length > 0;

  return (
    <div>
      <Header title="Dashboard" />
      <div className="p-6 flex flex-col gap-5">

        {/* Filtros */}
        <div className="flex flex-wrap gap-3 items-end">
          <FilterField label="Período">
            <MultiComboboxSelect
              large
              options={MESES}
              value={filters.mes_periodo}
              onChange={(v) => setFilters((f) => ({ ...f, mes_periodo: v }))}
              placeholder="Todos los períodos"
            />
          </FilterField>
          {(esAdmin || esViewer) && (
            <FilterField label="Vendedor">
              <MultiComboboxSelect
                large
                options={vendedores.map((v) => ({ value: v.id, label: v.nombre }))}
                value={filters.vendedor_id}
                onChange={(v) => setFilters((f) => ({ ...f, vendedor_id: v }))}
                placeholder="Todos los vendedores"
              />
            </FilterField>
          )}
          {hayFiltros && (
            <button
              onClick={() => setFilters({ mes_periodo: [], vendedor_id: [] })}
              className="self-end pb-2 text-xs text-gray-400 hover:text-gray-600 transition-colors"
            >
              Limpiar
            </button>
          )}
        </div>

        {isLoading ? (
          <p className="text-sm text-gray-400">Cargando...</p>
        ) : (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <KpiCard label="Total leads" value={data.totalLeads} />
              <KpiCard label="Conversiones" value={data.conversiones} sub={`Tasa: ${data.tasaConversion}%`} highlight />
              <KpiCard label="Rec. vencidos" value={data.recordatoriosVencidos} warn={data.recordatoriosVencidos > 0} />
              <KpiCard
                label="Tiempo promedio"
                value={data.tiempoPromedioGestion != null ? `${data.tiempoPromedioGestion}d` : '—'}
                sub="días hasta conversión"
              />
            </div>

            {/* Objetivo */}
            {objetivoMostrar > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                    Avance vs objetivo
                  </span>
                  <span className="text-xs text-gray-400">
                    {data.conversiones} / {objetivoMostrar} conversiones
                  </span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-1.5">
                  <div
                    className={`h-1.5 rounded-full transition-all duration-500 ${progresoMostrar >= 100 ? 'bg-emerald-500' : 'bg-[#1E9BE8]'}`}
                    style={{ width: `${progresoMostrar}%` }}
                  />
                </div>
                <p className="text-sm font-semibold text-gray-700 mt-2">{progresoMostrar}%</p>
              </div>
            )}

            {/* Gráficos */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

              {/* Temporal */}
              <div className="md:col-span-2 bg-white rounded-lg border border-gray-200 p-5">
                <SectionTitle>Leads por semana</SectionTitle>
                {data.temporal.length === 0 ? (
                  <p className="text-sm text-gray-400">Sin datos en este período.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={190}>
                    <AreaChart data={data.temporal} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                      <defs>
                        <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#0D2B5C" stopOpacity={0.12} />
                          <stop offset="95%" stopColor="#0D2B5C" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="fecha" tickFormatter={formatFechaSemana} tick={{ fontSize: 11, fill: '#9CA3AF', fontFamily: 'Inter' }} axisLine={false} tickLine={false} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#9CA3AF', fontFamily: 'Inter' }} axisLine={false} tickLine={false} />
                      <Tooltip formatter={(v) => [v, 'Leads']} labelFormatter={formatFechaSemana} {...tooltipStyle} />
                      <Area type="monotone" dataKey="count" stroke="#0D2B5C" strokeWidth={1.5} fill="url(#grad)" dot={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Origen */}
              <div className="bg-white rounded-lg border border-gray-200 p-5">
                <SectionTitle>Por origen</SectionTitle>
                {data.porOrigen.length === 0 ? (
                  <p className="text-sm text-gray-400">Sin datos.</p>
                ) : (
                  <>
                    <ResponsiveContainer width="100%" height={150}>
                      <PieChart>
                        <Pie data={data.porOrigen} dataKey="count" nameKey="origen" cx="50%" cy="50%" outerRadius={65} strokeWidth={0}>
                          {data.porOrigen.map((_, i) => (
                            <Cell key={i} fill={COLORES_ORIGEN[i % COLORES_ORIGEN.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v, n) => [v, n]} {...tooltipStyle} />
                      </PieChart>
                    </ResponsiveContainer>
                    {(() => {
                      const total = data.porOrigen.reduce((s, o) => s + o.count, 0);
                      return (
                        <div className="flex flex-col gap-1.5 mt-1">
                          {data.porOrigen.map((o, i) => (
                            <div key={o.origen} className="flex items-center gap-2 text-xs">
                              <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: COLORES_ORIGEN[i % COLORES_ORIGEN.length] }} />
                              <span className="text-gray-500">{o.origen}</span>
                              <span className="ml-auto font-medium text-gray-700">{o.count} ({total > 0 ? Math.round((o.count / total) * 100) : 0}%)</span>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>
            </div>

            {/* Por estado */}
            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <SectionTitle>Distribución por estado</SectionTitle>
              {data.porEstado.length === 0 ? (
                <p className="text-sm text-gray-400">Sin datos.</p>
              ) : (() => {
                const total = data.porEstado.reduce((s, e) => s + e.count, 0);
                return (
                  <ResponsiveContainer width="100%" height={Math.max(280, data.porEstado.length * 34)}>
                    <BarChart data={data.porEstado} layout="vertical" margin={{ left: 10, right: 80, top: 4, bottom: 4 }}>
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: '#9CA3AF', fontFamily: 'Inter' }} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="estado" width={155} tick={{ fontSize: 12, fill: '#374151', fontFamily: 'Inter' }} axisLine={false} tickLine={false} />
                      <Tooltip formatter={(v) => [`${v} (${total > 0 ? Math.round((v / total) * 100) : 0}%)`, 'Leads']} {...tooltipStyle} />
                      <Bar dataKey="count" radius={[0, 3, 3, 0]} maxBarSize={22}>
                        {data.porEstado.map((entry) => (
                          <Cell key={entry.estado} fill={COLORES_ESTADO[entry.estado] ?? '#D1D5DB'} />
                        ))}
                        <LabelList
                          dataKey="count"
                          position="right"
                          content={({ x, y, width, height, value }) => {
                            const pct = total > 0 ? Math.round((value / total) * 100) : 0;
                            return (
                              <text x={x + width + 6} y={y + height / 2} dominantBaseline="middle" fontSize={11} fontFamily="Inter" fill="#6B7280">
                                {value} ({pct}%)
                              </text>
                            );
                          }}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                );
              })()}
            </div>

            {/* Ranking vendedores */}
            {(esAdmin || esViewer) && data.vendedores.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 p-5">
                <SectionTitle>Ranking vendedores</SectionTitle>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left py-2 px-3 text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Vendedor</th>
                      <th className="text-right py-2 px-3 text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Leads</th>
                      <th className="text-right py-2 px-3 text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Ingresos</th>
                      <th className="text-right py-2 px-3 text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Verificados</th>
                      <th className="text-right py-2 px-3 text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Tasa</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.vendedores.map((v) => (
                      <tr key={v.vendedor_id ?? 'sin'} className="border-b border-gray-50 last:border-0">
                        <td className="py-2.5 px-3 text-gray-800 font-medium">{v.nombre}</td>
                        <td className="py-2.5 px-3 text-right text-gray-500">{v.totalLeads}</td>
                        <td className="py-2.5 px-3 text-right text-gray-500">{v.conversiones}</td>
                        <td className="py-2.5 px-3 text-right text-gray-500">{v.verificados}</td>
                        <td className="py-2.5 px-3 text-right text-gray-500">
                          {v.totalLeads > 0 ? `${Math.round((v.conversiones / v.totalLeads) * 100)}%` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function KpiCard({ label, value, sub, highlight, warn }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">{label}</p>
      <p className={`text-2xl font-semibold mt-2 ${highlight ? 'text-emerald-600' : warn ? 'text-red-500' : 'text-gray-900'}`}>
        {value}
      </p>
      {sub && <p className="text-[11px] text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

function SectionTitle({ children }) {
  return <h2 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-4">{children}</h2>;
}

function FilterField({ label, children }) {
  return (
    <div>
      <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-1.5">{label}</p>
      {children}
    </div>
  );
}
